import React, { useEffect, useRef, useState } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { getTestimonials } from '../api/client';

function StarRating({ rating }) {
  return (
    <div style={{ display: 'flex', gap: 3, marginBottom: 16 }}>
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={14}
          fill={i <= rating ? '#ffb700' : 'transparent'}
          color={i <= rating ? '#ffb700' : '#3a3a55'}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const ref = useRef();

  useEffect(() => {
    getTestimonials().then(setItems).catch(console.error);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { e.target.classList.add('visible'); obs.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Auto-rotate
  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx(p => (p + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;

  const prev = () => setIdx(p => (p - 1 + items.length) % items.length);
  const next = () => setIdx(p => (p + 1) % items.length);

  return (
    <div style={{ padding: '120px 0', background: 'var(--surface)', overflow: 'hidden' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 32px' }}>
        <div ref={ref} className='reveal' style={{ marginBottom: 64, position: 'relative' }}>
          <div className='ghost-number' style={{ position: 'absolute', left: -20, top: -60, zIndex: 0 }}>TM</div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#6a6a88', letterSpacing: 3, marginBottom: 12 }}>TESTIMONI</div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(36px,6vw,72px)', fontWeight: 700, color: '#f2f2ff', margin: 0, letterSpacing: '-2px', lineHeight: 1 }}>
              Kata Mereka<br />
              <span style={{ background: 'linear-gradient(135deg,#00e5ff,#9b00ff,#ff0099,#ffb700,#00e5ff)', backgroundSize: '400% 400%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'holo-shift 5s ease infinite' }}>Tentang Saya</span>
            </h2>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }} className='port-grid'>
          {items.map((t, i) => {
            const isActive = i === idx;
            return (
              <div key={t.id}
                style={{
                  background: isActive ? 'var(--card)' : 'rgba(13,13,26,0.5)',
                  border: '1px solid',
                  borderColor: isActive ? 'transparent' : 'rgba(255,255,255,0.05)',
                  backgroundImage: isActive
                    ? 'linear-gradient(var(--card),var(--card)) padding-box, linear-gradient(135deg,#00e5ff40,#9b00ff40) border-box'
                    : 'none',
                  borderRadius: 4, padding: '28px 24px',
                  opacity: isActive ? 1 : 0.5,
                  transform: isActive ? 'scale(1.02)' : 'scale(1)',
                  transition: 'all 0.4s ease',
                  cursor: 'none',
                }}
              >
                <Quote size={28} color={isActive ? '#00e5ff' : '#2a2a40'} style={{ marginBottom: 16, opacity: 0.7 }} />
                <StarRating rating={t.rating} />
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: isActive ? '#b0b0c8' : '#6a6a88', lineHeight: 1.8, margin: '0 0 24px', fontStyle: 'italic' }}>
                  "{t.content}"
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                    background: t.photo ? 'transparent' : 'linear-gradient(135deg,#00e5ff,#9b00ff)',
                    overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 14, color: '#07070f',
                  }}>
                    {t.photo
                      ? <img src={t.photo} alt={t.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : t.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 14, color: '#f2f2ff' }}>{t.name}</div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#6a6a88', letterSpacing: 0.5 }}>
                      {t.role}{t.role && t.company ? ' · ' : ''}{t.company}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Controls */}
        {items.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 32, justifyContent: 'center' }}>
            <button onClick={prev} style={{ width: 40, height: 40, borderRadius: 3, background: 'var(--card)', border: '1px solid rgba(255,255,255,0.08)', color: '#6a6a88', cursor: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,229,255,0.3)'; e.currentTarget.style.color = '#00e5ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#6a6a88'; }}
            ><ChevronLeft size={16} /></button>
            <div style={{ display: 'flex', gap: 6 }}>
              {items.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} style={{ width: i === idx ? 24 : 6, height: 6, borderRadius: 3, border: 'none', cursor: 'none', background: i === idx ? '#00e5ff' : 'rgba(255,255,255,0.15)', transition: 'all 0.3s', padding: 0 }} />
              ))}
            </div>
            <button onClick={next} style={{ width: 40, height: 40, borderRadius: 3, background: 'var(--card)', border: '1px solid rgba(255,255,255,0.08)', color: '#6a6a88', cursor: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,229,255,0.3)'; e.currentTarget.style.color = '#00e5ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#6a6a88'; }}
            ><ChevronRight size={16} /></button>
          </div>
        )}
      </div>
    </div>
  );
}
